import { ClipboardCopyButton } from '@front/components/ClipboardCopyButton';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { usePermissions } from '@front/hook/usePermissions';
import { Button, Checkbox, Loader, RightSheet, RightSheetContent, ScrollArea, Textarea, TextField, useToast } from '@plaidev/sour-react';
import { useProject } from 'auth-lib-front';
import { useEffect } from 'react';
import type {} from 'react/jsx-runtime';
import { Controller, useForm } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';

import SourceSystem = DomainV2.Import.SourceSystem;

export type SourceSystemEditSheetProps = {
  sourceSystemId: SourceSystem.Id | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type FormValues = {
  displayName: string;
  description: string;
};

const DEFAULT_VALUES: FormValues = { displayName: '', description: '' };

/**
 * @feature ソースシステム編集シート
 * @capabilities 表示名・説明の編集, IDコピー（システム管理者）
 * @behavior 一覧の行クリックで右からシートを表示, 表示名・説明を編集→「保存」で更新, システム管理者にはIDとコピーボタンを表示。プロジェクト管理者でない場合は閲覧のみ（フォーム無効化）
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
export function SourceSystemEditSheet({ sourceSystemId, open, onOpenChange }: SourceSystemEditSheetProps) {
  const toast = useToast();
  const { project } = useProject();
  const { isSystemAdmin, isProjectAdmin } = usePermissions();

  const { data, isPending } = useSourceSystem.Get(
    { projectId: project.id, id: sourceSystemId ?? undefined },
    { enabled: sourceSystemId !== null },
  );
  const updateMutation = useSourceSystem.Update();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!data) return;
    reset({ displayName: data.displayName, description: data.description ?? '' });
  }, [data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!data || !sourceSystemId) return;
    const trimmedName = values.displayName.trim();
    const trimmedDescription = values.description.trim();
    try {
      await updateMutation.mutateAsync({
        projectId: project.id,
        id: sourceSystemId,
        updateProps: {
          displayName: trimmedName,
          description: trimmedDescription === '' ? undefined : trimmedDescription,
        },
      });
      toast.success('保存しました');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新に失敗しました';
      toast.error(message);
    }
  });

  return (
    <RightSheet open={open} onOpenChange={onOpenChange}>
      <RightSheetContent>
        <div className="animate-slide-in-right-300 flex h-full w-[560px] max-w-[calc(100vw-24px)] flex-col overflow-hidden bg-default shadow-24">
          <div className="sticky top-0 left-0 z-10 flex items-center gap-x-2 border-b border-default bg-default px-4 py-[14px]">
            <h2 className="min-w-0 grow truncate text-[20px] leading-[30px] font-bold">{isProjectAdmin ? '編集' : '詳細'}</h2>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="basic" disabled={updateMutation.isPending} onClick={() => onOpenChange(false)} type="button">
                {isProjectAdmin ? 'キャンセル' : '閉じる'}
              </Button>
              {isProjectAdmin && (
                <Button
                  type="submit"
                  form="source-system-edit-form"
                  variant="primary"
                  loading={updateMutation.isPending}
                  disabled={!data || !isDirty || !isValid}
                >
                  保存
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="min-h-0 flex-1" orientation="vertical">
            <div className="px-4 py-6">
              {isPending || !data ? (
                <div className="flex min-h-40 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <form id="source-system-edit-form" className="flex flex-col gap-6" onSubmit={(e) => void onSubmit(e)}>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-default">
                      表示名<span className="text-negative">*</span>
                    </span>
                    <Controller
                      control={control}
                      name="displayName"
                      rules={{ validate: (v) => v.trim().length > 0 || '表示名を入力してください' }}
                      render={({ field }) => (
                        <TextField variant="contained" value={field.value} onChange={field.onChange} required disabled={!isProjectAdmin} />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-default">説明</span>
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <Textarea
                          className="[&>textarea]:w-full"
                          value={field.value}
                          onChange={field.onChange}
                          rows={4}
                          placeholder="説明を入力"
                          disabled={!isProjectAdmin}
                        />
                      )}
                    />
                  </div>
                  <div className="admin-ui flex flex-col gap-4 rounded-md px-3 py-2">
                    <Checkbox checked={data.isActive} disabled label="状態（APIでは変更不可）" />
                    {isSystemAdmin ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-default">ID</span>
                        <div className="inline-flex max-w-full flex-wrap items-center gap-1">
                          <span className="min-w-0 break-all font-mono text-xs text-muted-light">{data.id}</span>
                          <ClipboardCopyButton
                            ariaLabel="IDをクリップボードにコピー"
                            successToastMessage="IDをコピーしました"
                            text={data.id}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </form>
              )}
            </div>
          </ScrollArea>
        </div>
      </RightSheetContent>
    </RightSheet>
  );
}
